/**
 * The Speedy details panel (F-129; A plan §4.4 step 9; A3-ALIGN §1). From the bottom on a phone,
 * beside the video on desktop (the overlay places it). Read with `get_speedy_details` for the
 * viewer's current organisation, so state rules follow their business state.
 *
 * States: loading (skeleton), not available (the server returns nothing: unpublished, or paid for
 * a free member), error (Retry). Course and lesson links are read through the app's own course
 * hooks, so a link the viewer can't open is simply not shown.
 */
import { AlertTriangle, BookOpen, ExternalLink, PlayCircle, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useCourse, useLesson } from '@/api/hooks';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSpeedyDetails } from '@/hooks/use-speedies';
import type { SpeedyDetails } from '@/api/speedies';

export type DetailsTarget =
  | { kind: 'course'; courseId: string }
  | { kind: 'lesson'; courseId: string; lessonId: string }
  | { kind: 'speedy'; speedyId: string };

interface SpeedyDetailsPanelProps {
  speedyId: string;
  /** Open scrolled to this section (the safety icon opens it at Safety). */
  focus?: 'safety';
  canReport: boolean;
  onReport: () => void;
  onClose: () => void;
  /** A link was chosen: the player closes first, then goes there. */
  onGo: (target: DetailsTarget) => void;
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

export function SpeedyDetailsPanel({ speedyId, focus, canReport, onReport, onClose, onGo }: SpeedyDetailsPanelProps) {
  const { organization } = useOrganization();
  const { data, isLoading, isError, refetch, isFetching } = useSpeedyDetails(speedyId, organization?.id ?? null);
  const safetyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focus === 'safety' && data) safetyRef.current?.scrollIntoView({ block: 'start' });
  }, [focus, data]);

  return (
    <section
      className="flex h-full flex-col bg-card text-foreground"
      aria-label="Speedy details"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Details</h2>
        <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-secondary" aria-label="Close details">
          <X className="h-5 w-5" aria-hidden />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-4 text-sm">
        {isLoading && (
          <div className="space-y-3" role="status" aria-label="Loading details">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {isError && (
          <div className="space-y-3 text-center">
            <p className="text-muted-foreground">Couldn't load the details.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="min-h-[44px] rounded-full border border-border px-4 font-semibold disabled:opacity-50"
            >
              {isFetching ? 'Trying…' : 'Try again'}
            </button>
          </div>
        )}
        {!isLoading && !isError && !data && (
          <p className="text-center text-muted-foreground">This Speedy isn't available.</p>
        )}
        {data && <DetailsBody d={data} safetyRef={safetyRef} onGo={onGo} />}
      </div>
      {canReport && data && (
        <footer className="border-t border-border px-4 py-3">
          <button type="button" onClick={onReport} className="min-h-[44px] text-sm text-muted-foreground underline-offset-2 hover:underline">
            Report this Speedy
          </button>
        </footer>
      )}
    </section>
  );
}

function List({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <ul className="list-disc space-y-0.5 pl-5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
}

function DetailsBody({
  d,
  safetyRef,
  onGo,
}: {
  d: SpeedyDetails;
  safetyRef: React.RefObject<HTMLDivElement>;
  onGo: (t: DetailsTarget) => void;
}) {
  const topic = [d.card.group_label, d.job_label].filter(Boolean).join(' · ');
  return (
    <div className="space-y-5">
      <div>
        <p className="text-base font-semibold">{d.card.title ?? 'Untitled Speedy'}</p>
        {d.card.caption && <p className="mt-1 text-muted-foreground">{d.card.caption}</p>}
        {topic && <p className="mt-2 text-xs text-muted-foreground">{topic}</p>}
        {d.states && d.states.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">Mentions rules for {d.states.join(', ')}</p>
        )}
      </div>
      {d.problem_solved && <p><span className="font-semibold">Problem solved: </span>{d.problem_solved}</p>}
      <List title="Steps" items={d.processes} />
      <List title="Materials" items={d.materials} />
      <List title="Tools" items={d.tools} />
      {d.safety.length > 0 && (
        <div ref={safetyRef}>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Safety</h3>
          <ul className="space-y-1">
            {d.safety.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                {s.high_risk && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-label="High-risk work" />}
                <span>{s.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {d.regulations.length > 0 && (
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Regulations</h3>
          <ul className="space-y-1">
            {d.regulations.map((r, i) => (
              <li key={i}>
                {r.source_url
                  ? (
                    <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline-offset-2 hover:underline">
                      {r.label} <ExternalLink className="h-3 w-3" aria-hidden />
                    </a>
                  )
                  : r.label}
                {r.states && r.states.length > 0 && <span className="text-muted-foreground"> ({r.states.join(', ')})</span>}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">General information. Check with a licensed professional.</p>
        </div>
      )}
      {d.chapters.length > 0 && (
        <List title="Chapters" items={d.chapters.map((c) => `${fmtTime(c.start_s)}  ${c.title}`)} />
      )}
      {d.links.length > 0 && (
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Keep learning</h3>
          <ul className="space-y-1">
            {d.links.map((l) => <LinkRow key={`${l.type}:${l.id}`} link={l} onGo={onGo} />)}
          </ul>
        </div>
      )}
      {d.transcript.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">Transcript</summary>
          <div className="mt-2 space-y-1 text-muted-foreground">
            {d.transcript.map((t, i) => (
              <p key={i}><span className="tabular-nums">{fmtTime(t.start_s)}</span> {t.text}</p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/** One link. Course and lesson titles come from the app's own reads; unreadable ones don't show. */
function LinkRow({ link, onGo }: { link: SpeedyDetails['links'][number]; onGo: (t: DetailsTarget) => void }) {
  const course = useCourse(link.type === 'course' ? link.id : '');
  const lesson = useLesson(link.type === 'lesson' ? link.id : '');
  let title: string | null = null;
  let target: DetailsTarget | null = null;
  if (link.type === 'speedy') {
    title = link.title;
    target = { kind: 'speedy', speedyId: link.id };
  } else if (link.type === 'course' && course.data) {
    title = (course.data as { title?: string }).title ?? null;
    target = { kind: 'course', courseId: link.id };
  } else if (link.type === 'lesson' && lesson.data) {
    const l = lesson.data as { title?: string; course_id?: string };
    title = l.title ?? null;
    target = l.course_id ? { kind: 'lesson', courseId: l.course_id, lessonId: link.id } : null;
  }
  if (!title || !target) return null;
  const Icon = link.type === 'speedy' ? PlayCircle : BookOpen;
  return (
    <li>
      <button type="button" onClick={() => onGo(target!)} className="flex min-h-[44px] w-full items-center gap-2 text-left hover:underline">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span>{title}</span>
      </button>
    </li>
  );
}
